import { reduceBucket, reduceEnvelopeBucket } from "./aggregateSeries.helpers";
import { findLastIndexWhere } from "./binarySearch";
import type { AggregationMode } from "./trendingUrlState";

export interface EnvelopeBand {
	min: [number, number | null][];
	max: [number, number | null][];
}

export interface AggregatedSeries {
	data: [number, number | null][];
	band?: EnvelopeBand;
	decimated: boolean;
	visibleCount: number;
}

export const aggregateSeries = (
	data: [number, number | null][],
	visibleStart: number,
	visibleEnd: number,
	targetPoints: number,
	mode: AggregationMode,
): AggregatedSeries => {
	if (data.length === 0) {
		return { data, decimated: false, visibleCount: 0 };
	}

	const lastIdx = data.length - 1;

	const leftAnchorIdx = findLastIndexWhere(
		data,
		(point) => point[0] < visibleStart,
	);
	const lastVisibleIdx = findLastIndexWhere(
		data,
		(point) => point[0] <= visibleEnd,
	);

	if (lastVisibleIdx < 0) {
		return { data: data.slice(0, 1), decimated: false, visibleCount: 0 };
	}

	const rightAnchorIdx = lastVisibleIdx < lastIdx ? lastVisibleIdx + 1 : -1;

	const hasLeftAnchor = leftAnchorIdx >= 0;
	const hasRightAnchor = rightAnchorIdx >= 0;

	const firstVisibleIdx = leftAnchorIdx + 1;
	const visibleCount = Math.max(0, lastVisibleIdx - leftAnchorIdx);

	const keptFrom = hasLeftAnchor ? leftAnchorIdx : firstVisibleIdx;
	const keptTo = hasRightAnchor ? rightAnchorIdx : lastVisibleIdx;

	if (keptTo < keptFrom) {
		return {
			data: data.slice(keptFrom, keptFrom + 1),
			decimated: false,
			visibleCount,
		};
	}

	const isEnvelopeMode = mode === "envelope";

	if (visibleCount <= targetPoints || targetPoints < 4) {
		const slice = data.slice(keptFrom, keptTo + 1);
		return {
			data: slice,
			band: isEnvelopeMode ? { min: slice, max: slice } : undefined,
			decimated: false,
			visibleCount,
		};
	}

	const bucketCount = targetPoints;

	const result: [number, number | null][] = [];
	const bandMin: [number, number | null][] = [];
	const bandMax: [number, number | null][] = [];

	const pushAnchorToBand = (index: number) => {
		const [timestamp, value] = data[index];
		bandMin.push([timestamp, value]);
		bandMax.push([timestamp, value]);
	};

	if (hasLeftAnchor) {
		result.push(data[leftAnchorIdx]);

		if (isEnvelopeMode) {
			pushAnchorToBand(leftAnchorIdx);
		}
	}

	if (visibleCount > 0) {
		const baseSize = Math.floor(visibleCount / bucketCount);
		const remainder = visibleCount % bucketCount;

		for (let bucket = 0; bucket < bucketCount; bucket++) {
			const bucketSize = baseSize + (bucket < remainder ? 1 : 0);

			if (bucketSize === 0) {
				continue;
			}

			const startIdx =
				firstVisibleIdx + bucket * baseSize + Math.min(bucket, remainder);
			const endIdx = Math.min(startIdx + bucketSize - 1, lastVisibleIdx);

			if (isEnvelopeMode) {
				const { avg, min, max } = reduceEnvelopeBucket(data, startIdx, endIdx);
				result.push(avg);
				bandMin.push(min);
				bandMax.push(max);
			} else {
				result.push(reduceBucket(data, startIdx, endIdx, mode));
			}
		}
	}

	if (hasRightAnchor) {
		result.push(data[rightAnchorIdx]);

		if (isEnvelopeMode) {
			pushAnchorToBand(rightAnchorIdx);
		}
	}

	return {
		data: result,
		band: isEnvelopeMode ? { min: bandMin, max: bandMax } : undefined,
		decimated: true,
		visibleCount,
	};
};
